import { Typography } from 'antd';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { SalesforceObject } from 'api/integrations/salesforce';
import { LinkButton } from 'components/atoms/button';
import { Modal } from 'components/atoms/modal';
import { Select } from 'components/atoms/select';
import { BlockSpace } from 'components/atoms/space';
import { Divider, PlaceholderText } from '../commons/styles';
import { ItemLayout } from '../layout';
import { CreateProps, StatusTypes } from '../use-automation-create';

const { Text } = Typography;

interface SalesforceObjectType extends Omit<CreateProps, 'integrationId'> {
  data?: SalesforceObject[];
  apiStatus: StatusTypes;
}

export const SalesForceObject = ({
  automationDetails,
  data,
  apiStatus,
  onChange,
}: SalesforceObjectType): JSX.Element => {
  const [showModal, setShowModal] = useState(false);

  const [selected, setSelected] =
    useState<{ label: string; value: string } | undefined>(undefined);

  const transformedOptions = useMemo(
    () =>
      data?.map((salseforceObject) => ({
        label: salseforceObject.name,
        value: salseforceObject.id,
      })),
    [data],
  );

  const handleSelection = () => {
    onChange({
      automationId: Number(automationDetails?.automationId),
      automationName: automationDetails?.automationName,
      description: automationDetails?.description,
      salesforceObject: selected,
    });
    setShowModal(false);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelected(
      transformedOptions?.find(
        (so) => so.value === automationDetails?.salesforceObject?.value,
      ) ?? undefined,
    );
  };

  const Content = () => (
    <BlockSpace direction="vertical" size={16}>
      {automationDetails?.salesforceObject ? (
        <Text strong>{automationDetails?.salesforceObject.label}</Text>
      ) : (
        <PlaceholderText>
          <FormattedMessage
            id="settings.integrations.sf.automation.create.object.placeholder"
            values={{
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        </PlaceholderText>
      )}

      <LinkButton
        loading={apiStatus === 'loading'}
        onClick={() => setShowModal(true)}
        type="link"
      >
        <FormattedMessage id="edit" />
        ...
      </LinkButton>

      <Divider />
    </BlockSpace>
  );

  return (
    <div>
      <ItemLayout
        right={<Content />}
        left={
          <FormattedMessage id="settings.integrations.sf.automation.create.object.title" />
        }
        required
      />

      {showModal ? (
        <Modal
          onCancel={handleClose}
          onOk={handleSelection}
          title={
            <FormattedMessage id="settings.integrations.sf.automation.create.object.modal.title" />
          }
          visible={showModal}
          okText={<FormattedMessage id="save" />}
          okButtonProps={{ disabled: apiStatus === 'loading' }}
          destroyOnClose
        >
          <Select
            isClearable
            options={transformedOptions}
            onChange={setSelected}
            value={selected}
            isLoading={apiStatus === 'loading'}
          />
        </Modal>
      ) : null}
    </div>
  );
};
